/**
 * Helpers to preserve the intended URL when redirecting unauthenticated users to login,
 * and to navigate back to that URL after successful login (email or SSO).
 */

export const REDIRECT_QUERY_PARAM = 'redirect';

/**
 * Build the login page URL with the current path + search preserved in a redirect query param.
 * Call this when redirecting an unauthenticated user to /login so they can be sent back after login.
 * Skips adding redirect if pathname is already /login to avoid loops.
 */
export function buildLoginUrlWithRedirect(pathname: string, search: string): string {
  if (pathname === '/login') {
    return '/login';
  }
  const intended = pathname + search;
  if (!intended || intended === '/') {
    return '/login';
  }
  const encoded = encodeURIComponent(intended);
  return `/login?${REDIRECT_QUERY_PARAM}=${encoded}`;
}

/**
 * Parse the redirect URL from the login page's query string.
 * Returns a safe internal path (must start with single "/", not "//") to avoid open redirects.
 * Returns null if missing or invalid.
 */
export function getRedirectPathAfterLogin(search: string): string | null {
  const params = new URLSearchParams(search);
  const raw = params.get(REDIRECT_QUERY_PARAM);
  if (!raw) return null;
  try {
    const path = decodeURIComponent(raw);
    // Allow only same-origin paths: must start with "/" but not "//"
    if (path.startsWith('//') || !path.startsWith('/')) {
      return null;
    }
    return path;
  } catch {
    return null;
  }
}
